import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Mail, ExternalLink, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function EmailSettings() {
  const [form, setForm] = useState({ imap_host: "imap.gmail.com", imap_port: 993, email: "", app_password: "", enabled: true });
  const [configured, setConfigured] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);

  const load = async () => {
    const { data } = await api.get("/email/settings");
    if (data.configured) {
      setConfigured(true);
      setForm((f) => ({ ...f, imap_host: data.imap_host, imap_port: data.imap_port, email: data.email, enabled: data.enabled, app_password: "" }));
      setLastFetch(data.last_fetch_at);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!form.app_password && configured) {
        toast.error("Bitte App-Passwort erneut eingeben zum Speichern");
        setSaving(false);
        return;
      }
      await api.put("/email/settings", form);
      toast.success("E-Mail-Anbindung gespeichert");
      setForm((f) => ({ ...f, app_password: "" }));
      setConfigured(true);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const testFetch = async () => {
    setFetching(true);
    try {
      const { data } = await api.post("/email/fetch");
      toast.success(`${data.new_count} neue Nachricht(en), ${data.matched_count} automatisch zugeordnet`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Abruf fehlgeschlagen");
    } finally {
      setFetching(false);
    }
  };

  const removeSettings = async () => {
    if (!window.confirm("E-Mail-Anbindung wirklich entfernen?")) return;
    await api.delete("/email/settings");
    setConfigured(false);
    setForm({ imap_host: "imap.gmail.com", imap_port: 993, email: "", app_password: "", enabled: true });
    toast.success("Anbindung entfernt");
  };

  return (
    <div className="p-8 space-y-6 animate-fade-up max-w-3xl" data-testid="email-settings-page">
      <div>
        <div className="overline">E-Mail-Anbindung</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Posteingang-Einstellungen</h1>
        <p className="text-muted-foreground mt-1">
          Verbinde ein Gmail-Postfach — eingehende Netzbetreiber-Mails werden automatisch dem passenden Kunden zugeordnet.
        </p>
      </div>

      <Card className="border-border">
        <CardContent className="p-6">
          <div className="flex items-start gap-3 mb-6">
            <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <div className="text-sm">
              <div className="font-medium">Gmail App-Passwort erstellen</div>
              <p className="text-muted-foreground mt-1">
                Für Gmail benötigst du ein sogenanntes „App-Passwort" (16 Zeichen). 2-Faktor-Authentifizierung muss aktiv sein.
              </p>
              <a
                href="https://myaccount.google.com/apppasswords"
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-primary hover:underline"
                data-testid="link-google-apppw"
              >
                Google App-Passwort erstellen <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>

          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="email">Gmail-Adresse</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="netzanmeldung@kayser.de" data-testid="email-input" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="pw">App-Passwort {configured && <span className="text-xs text-muted-foreground">(erneut eingeben zum Speichern)</span>}</Label>
              <Input id="pw" type="password" value={form.app_password} onChange={(e) => setForm({ ...form, app_password: e.target.value })} placeholder={configured ? "•••• •••• •••• ••••" : "16 Zeichen ohne Leerzeichen"} data-testid="email-pw-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="host">IMAP-Host</Label>
              <Input id="host" value={form.imap_host} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} data-testid="email-host-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="port">Port</Label>
              <Input id="port" type="number" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: parseInt(e.target.value || "993") })} data-testid="email-port-input" />
            </div>
            <div className="col-span-2 flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <div className="text-sm font-medium">Aktiv</div>
                <div className="text-xs text-muted-foreground">Neue Nachrichten werden abgerufen</div>
              </div>
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="email-enabled" />
            </div>
            <div className="col-span-2 flex gap-2 mt-2">
              <Button type="submit" disabled={saving} data-testid="email-save">
                {saving ? "Speichern …" : configured ? "Aktualisieren" : "Verbinden"}
              </Button>
              {configured && (
                <>
                  <Button type="button" variant="outline" onClick={testFetch} disabled={fetching} data-testid="email-test-fetch">
                    <RefreshCw className={`h-4 w-4 mr-2 ${fetching ? "animate-spin" : ""}`} /> Jetzt abrufen
                  </Button>
                  <Button type="button" variant="ghost" onClick={removeSettings} data-testid="email-remove">
                    <Trash2 className="h-4 w-4 mr-2 text-destructive" /> Trennen
                  </Button>
                </>
              )}
            </div>
          </form>

          {configured && lastFetch && (
            <div className="mt-4 text-xs text-muted-foreground border-t border-border pt-3">
              Letzter Abruf: {new Date(lastFetch).toLocaleString("de-DE")}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
